import { css } from '@emotion/core'
import styled from '@emotion/styled'

import { Color } from '../../enums/color'
import { DefaultValue } from '../../enums/defaultValue'
import { FontSize } from '../../enums/fontSize'
import { LineHeight } from '../../enums/lineHeight'
import { Asable } from '../../types/asable'
import { Themable } from '../../types/themable'
import { getDefaultTransition } from '../../utils/getDefaultTransition'
import { getTruncateStyle } from '../../utils/getTruncateStyle'
import { Alignment } from './types/alignment'
import { Weight } from './types/weight'
import { weightToValue } from './utils/weightToValue'

interface TextProps extends Asable, Themable {
  alignment?: Alignment
  fontFamily?: string
  inherit?: boolean
  inheritColor?: boolean
  inline?: boolean
  lowercase?: boolean
  nowrap?: boolean
  selectable?: boolean
  strikethrough?: boolean
  textColor?: Color
  truncate?: boolean
  uppercase?: boolean
  weight?: Weight
}

const inlineStyle = css`
  display: inline;
`

const noWrapStyle = css`
  white-space: nowrap;
`

const uppercaseStyle = css`
  text-transform: uppercase;
`

const lowercaseStyle = css`
  text-transform: lowercase;
`

const strikethroughStyle = css`
  text-decoration: line-through;
`

const getCommonStyles = ({
  alignment = 'inherit',
  fontFamily,
  inherit,
  inheritColor,
  inline,
  lowercase,
  nowrap,
  selectable,
  strikethrough,
  textColor,
  truncate,
  uppercase,
  weight,
}: TextProps) => css`
  display: block;
  color: ${(inherit || inheritColor) && !textColor ? 'inherit' : textColor};
  font-family: ${inherit && !fontFamily ? 'inherit' : fontFamily || DefaultValue.FontFamily};
  font-weight: ${inherit && !weight ? 'inherit' : weightToValue[weight || 'regular']};
  text-align: ${inherit && !alignment ? 'inherit' : alignment};
  user-select: ${selectable ? 'auto' : 'none'};
  ${inherit ? 'font-size: inherit' : ''};
  ${!inherit && !inheritColor && getDefaultTransition('color')};

  ${nowrap && noWrapStyle};
  ${inline && inlineStyle};
  ${uppercase && uppercaseStyle};
  ${lowercase && lowercaseStyle};
  ${strikethrough && strikethroughStyle};
  ${truncate && getTruncateStyle()};
`

export const Display = styled.h1<TextProps>`
  font-size: ${FontSize.TextDisplay};
  line-height: ${LineHeight.TextDisplay};
  ${({ weight = 'bold', ...restProps }) => getCommonStyles({ weight, ...restProps })}
`

export const TextH1 = styled.h1<TextProps>`
  font-size: ${FontSize.TextH1};
  line-height: ${LineHeight.TextH1};
  ${({ weight = 'semiBold', ...restProps }) => getCommonStyles({ weight, ...restProps })}
`

export const TextH2 = styled.h2<TextProps>`
  font-size: ${FontSize.TextH2};
  line-height: ${LineHeight.TextH2};
  ${({ weight = 'semiBold', ...restProps }) => getCommonStyles({ weight, ...restProps })}
`

export const TextH3 = styled.h3<TextProps>`
  font-size: ${FontSize.TextH3};
  line-height: ${LineHeight.TextH3};
  ${({ weight = 'semiBold', ...restProps }) => getCommonStyles({ weight, ...restProps })}
`

export const TextH4 = styled.h4<TextProps>`
  font-size: ${FontSize.TextH4};
  line-height: ${LineHeight.TextH4};
  ${({ weight = 'semiBold', ...restProps }) => getCommonStyles({ weight, ...restProps })}
`

export const TextBodyLG = styled.p<TextProps>`
  font-size: ${FontSize.TextBodyLG};
  line-height: ${LineHeight.TextBodyLG};
  ${(props) => getCommonStyles(props)}
`

export const TextBodyMD = styled.p<TextProps>`
  font-size: ${FontSize.TextBodyMD};
  line-height: ${LineHeight.TextBodyMD};
  ${(props) => getCommonStyles(props)}
`

export const TextBodySM = styled.p<TextProps>`
  font-size: ${FontSize.TextBodySM};
  line-height: ${LineHeight.TextBodySM};
  ${(props) => getCommonStyles(props)}
`
